/**
 * Lead attribution utilities for SCL Forms.
 *
 * Two sources of attribution data:
 *
 *   1) URL-derived (PER-SESSION, LAST-TOUCH): UTMs, ad click IDs and the landing
 *      page URL are captured by the master-page snippet and stored in
 *      sessionStorage under `scl_utm`. sessionStorage is scoped to the current
 *      browsing session (tab), which gives exactly the required behaviour:
 *        - retained across page navigation until the form is submitted,
 *        - a fresh visit (e.g. days later) starts empty, so an organic/direct
 *          return sends BLANKS instead of stale values,
 *        - a visit that arrives with new params overwrites the old (last-touch).
 *
 *   2) Cookie-derived (LIVE at submit): _fbp, _fbc and the GA4 client id (from the
 *      _ga cookie) are set/maintained by the Meta Pixel and GA4 tags, so they are
 *      read straight from document.cookie at submit time. _fbc only exists when the
 *      visitor arrived with an fbclid; _fbp/_ga exist for all visitors (organic too).
 *      Blank values are expected and are not errors.
 *
 * getAttributionParams() merges both into the camelCase payload the backend and the
 * Liferay object expect. Anything not captured is returned as "" (blank).
 *
 * NOTE: STORAGE_KEY and the stored field names below MUST stay in sync with the
 * master-page capture snippet.
 */
import { getCookie } from './formsCookie';

const STORAGE_KEY = 'scl_utm';

// URL values captured per-session (last-touch) by the master-page snippet.
// Map of payload key (camelCase) -> stored key (snake_case, as written by the snippet).
const STORED_FIELDS = {
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmTerm: 'utm_term',
  utmContent: 'utm_content',
  gclid: 'gclid',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  fbclid: 'fbclid',
  msclkid: 'msclkid',
  campaignId: 'campaign_id',
  landingPageUrl: 'landing_page_url',
};

function emptyStored() {
  const out = {};
  Object.keys(STORED_FIELDS).forEach((k) => {
    out[k] = '';
  });
  return out;
}

function readStored() {
  try {
    if (typeof sessionStorage === 'undefined') {
      return emptyStored();
    }
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return emptyStored();
    }
    const data = JSON.parse(raw);
    if (!data) {
      return emptyStored();
    }
    const out = {};
    Object.entries(STORED_FIELDS).forEach(([payloadKey, storeKey]) => {
      out[payloadKey] = data[storeKey] || '';
    });
    return out;
  } catch (e) {
    return emptyStored();
  }
}

/**
 * GA4 client id from the _ga cookie.
 * _ga looks like "GA1.1.<clientId>" where clientId is the last two dot-segments,
 * e.g. "GA1.1.555948622.1788191274" -> "555948622.1788191274".
 */
function getGaClientId() {
  const ga = getCookie('_ga');
  if (!ga) {
    return '';
  }
  const parts = ga.split('.');
  if (parts.length < 4) {
    return '';
  }
  return parts.slice(-2).join('.');
}

function readCookies() {
  return {
    fbp: getCookie('_fbp') || '',
    fbc: getCookie('_fbc') || '',
    gaClientId: getGaClientId(),
  };
}

/**
 * Full attribution set for the lead payload (camelCase keys).
 * Combines per-session URL values (sessionStorage) with live cookie values.
 *
 * Client rule: _fbc must be populated ONLY when this visit arrived with an fbclid.
 * The _fbc cookie can survive ~90 days from an earlier Meta click, so it is forced
 * blank whenever the current session has no fbclid (Google / Bing / Organic).
 */
export function getAttributionParams() {
  const stored = readStored();
  const cookies = readCookies();

  if (!stored.fbclid) {
    cookies.fbc = '';
  }

  return {
    ...stored,
    ...cookies,
  };
}

/**
 * Clear the stored URL values. Call ONLY after a successful submit so the captured
 * attribution is consumed by exactly one lead. Cookie values are owned by the
 * GA/Meta tags and are intentionally left untouched.
 */
export function clearAttributionParams() {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {
    /* sessionStorage unavailable — ignore */
  }
}
